import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Icon from '../Icon'
import Griddle, { RowDefinition, ColumnDefinition, plugins } from 'griddle-react'
import ColumnChooser from './components/ColumnChooser'
import PageSizeChooser from './components/PageSizeChooser'
import Search from './components/Search'
import Settings from './components/Settings'
import Tether from '../Tether'
import Input from '../Input'
import styles from './Table.css'

/* See griddle API docs http://griddlegriddle.github.io/Griddle/docs/api/ */
const propTypes = {
  /** Data supplied to table. Must be an array of objects */
  data: PropTypes.array,
  /** Column settings. Must be an array of objects or string of object key */
  columns: PropTypes.array,
  /** Set loading state */
  isLoading: PropTypes.bool,
  /** if false, columns are not sortable */
  isSortable: PropTypes.bool,
  /** if false, table is not searchable */
  isSearchable: PropTypes.bool,
  /** Default number of items per page */
  itemsPerPage: PropTypes.number,
  /** If false, no table settings will be shown */
  hasSettings: PropTypes.bool,
  /** Custom CSS Classes */
  className: PropTypes.string,
  /** Run function on row click */
  onRowClick: PropTypes.func,
  /** Run function on row hover */
  onRowMouseEnter: PropTypes.func,
  /** Run function on row mouseLeave */
  onRowMouseLeave: PropTypes.func,
  /** Run function on table search */
  onFilter: PropTypes.func,
  /** Run function on table sort */
  onSort: PropTypes.func,
  /** Run function on table next page */
  onNext: PropTypes.func,
  /** Run function on table previous page  */
  onPrevious: PropTypes.func,
  /** Run function on table page change */
  onGetPage: PropTypes.func,
  /** Set inline Column components */
  children: PropTypes.any,
}

const defaultProps = {
  data: [],
  hasSettings: true,
  itemsPerPage: 5,
  isSortable: true,
}

const stylePlugin = {
  styleConfig: {
    icons: {
      TableHeadingCell: {
        sortDescendingIcon: <span className={styles.down}>▼</span>,
        sortAscendingIcon: <span className={styles.up}>▲</span>
      },
    },
    classNames: {
      Layout: styles.layout,
      Table: styles.table,
      Row: styles.row,
      Settings: styles.settingTrigger,
      TableHeadingCell: styles.heading,
      NoResults: styles.noResults,
      PageDropdown: styles.pageDropdown,
      TableHeadingCellAscending: styles.sortAscending,
      TableHeadingCellDescending: styles.sortDescending,
    },
    // styles: {}
  }
}

const emptyComponent = () => {
  return null
}

export default class Table extends React.Component {
  constructor (props, context) {
    super(props, context)
    this.state = {
      settingsOpen: false
    }
  }
  componentDidMount() {
    // const bounds = this.table.getBoundingClientRect()
    // console.log(this.table.querySelectorAll('table')[0])
    // const selectOptions = this.table.querySelectorAll('.griddle-pagination select option')
    // tableElement.style.minHeight = `${bounds.height}px`
  }
  shouldComponentUpdate(nextProps, nextState) {
    // This is to fix a bug where state change in any of page's components will trigger a perpetual loading state for <Table>
    // TODO: Find a better way to fix this
    return !(JSON.stringify(nextProps) === JSON.stringify(this.props) && JSON.stringify(nextState) === JSON.stringify(this.state))
  }
  render() {
    const {
      data,
      columns,
      className,
      isLoading,
      isSortable,
      isSearchable,
      hasSettings,
      itemsPerPage,
      onRowClick,
      onRowMouseEnter,
      onRowMouseLeave,
      onFilter,
      onSort,
      onNext,
      onPrevious,
      onGetPage,
      children
    } = this.props

    const tableData = data

    const griddlePlugins = [
      plugins.LocalPlugin,
      stylePlugin
    ]
    const classes = classNames(
      'component-table',
      styles.wrapper,
      className
    )

    let Columns
    if (children) {
      // inline <Column> components used
      Columns = children
    } else if (columns && Array.isArray(columns)) {
      // prop columns set. use to create table
      Columns = columns.map((column, i) => {
        const order = column.order || i + 1
        if (typeof column === 'string') {
          return <ColumnDefinition key={`${i}-${column}`} id={column} order={order} />
        }
        const isVisible = column.visible || true
        let sortableSettings = column.sortable
        if (isSortable === false) {
          sortableSettings = false
        }
        return (
          <ColumnDefinition
            {...column}
            key={`${i}-${column}`}
            id={column.id}
            order={order}
            sortable={sortableSettings}
          />
        )
      })

    } else {
      // no column data set. Auto calculate based on data supplied
      if (tableData && tableData.length) {
        // Auto columize table from first object in array
        Columns = Object.keys(tableData[0]).map((key, i) => {
          const order = i + 1
          return <ColumnDefinition key={key} id={key} order={order} sortable={isSortable} />
        })
      }
    }

    const Loading = ({ className, style }) => (
      <div style={style} className={className}>
        Loading…
      </div>
    );

    if (isLoading) {
      return <Loading />
    }

    const SettingsToggle = (props) =>  {
      if (!hasSettings) {
        return null
      }
      return (
        <div className={styles.settingsToggle} onClick={props.toggleSettings}>
          <Icon name='settings' size={35} />
        </div>
      )
    }

    let customComponents = {
      SettingsToggle: SettingsToggle,
      Settings: Settings,
      Filter: Search,
      SettingsComponents: {
        columnChooser: ColumnChooser,
        pageSizeSettings: PageSizeChooser,
      },
      Loading: Loading
    }

    if (data.length <= itemsPerPage) {
      // data shorter than one page. Hide pagination
      customComponents.Pagination = ({ Next, Previous, PageDropdown }) => {
        return null
      }
    }

    if (isSearchable === false) {
      customComponents.Filter = emptyComponent
    }

    if (onRowClick || onRowMouseEnter || onRowMouseLeave) {
      customComponents.RowEnhancer = OriginalComponent => props => {
        const clickHandler = (onRowClick) ? () => onRowClick(props) : null
        const mouseEnterHandler = (onRowMouseEnter) ? () => onRowMouseEnter(props) : null
        const mouseLeaveHandler = (onRowMouseLeave) ? () => onRowMouseLeave(props) : null
        return (
          <OriginalComponent
            {...props}
            onClick={clickHandler}
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
          />
        )
      }
    }

    return (
      <div className={classes} ref={(div) => { this.table = div }}>
        <Griddle data={tableData} plugins={griddlePlugins}
          pageProperties={{
            pageSize: itemsPerPage
          }}
          events={{
            onFilter: onFilter,
            onSort: onSort,
            onNext: onNext,
            onPrevious: onPrevious,
            onGetPage: onGetPage
          }}
          components={customComponents}
        >
          <RowDefinition>
            {Columns}
          </RowDefinition>
        </Griddle>
      </div>
    )
  }
}

Table.propTypes = propTypes
Table.defaultProps = defaultProps

export { ColumnDefinition as Column }



// WEBPACK FOOTER //
// ../src/components/Table/Table.js